// Copied from MedSnapp Bleep (viewers/ecg/synth.ts), background IP licensed to MedSnapp; keep in sync by copying, do not fork.
// Rounds, 16 Oct 2026: the broad complex (VT, PEA) now has a shape of its own in every lead, projected from two ventricular
// vectors (broadMorph); the beat's support and the QT the paper can draw were widened for a PR up to 400 ms and a long QT.
// Copy this file back to Bleep to keep the two in step.
/**
 * MedSnapp Bleep — deterministic ECG synthesis.
 *
 * Sum-of-Gaussians PQRST per beat, projected onto the 12 leads through a per-lead amplitude
 * table, with ST shift, T inversion, axis and a rhythm state machine (sinus with HRV, AF with
 * an irregularly irregular RR and fibrillatory baseline, monomorphic VT, VF as filtered noise,
 * PEA as slow wide complexes, asystole as near-flat drift). A broad complex is two ventricular
 * vectors projected onto each lead's own direction, so every lead has its own shape (a broad
 * monophasic R, an RS, a QS) with the T wave discordant to it.
 *
 * Everything here is pure and deterministic: the same (params, t, lead) always gives the same
 * millivolt value, so the paper is reproducible and tests are stable. No DOM, no timers.
 */
import type { EcgParams, Lead, Rhythm } from './types';

export const LEADS: readonly Lead[] = ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'];

const TAU = Math.PI * 2;
const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
const lerp = (a: number, b: number, m: number) => a + (b - a) * m;
const smoothstep = (x: number) => { const u = clamp(x, 0, 1); return u * u * (3 - 2 * u); };
const g = (u: number, a: number, c: number, s: number) => { const d = (u - c) / s; return a * Math.exp(-0.5 * d * d); };

// ----------------------------------------------------------------------------
// Deterministic noise
// ----------------------------------------------------------------------------

/** Integer hash to [0, 1). Deterministic across platforms. */
export function hash01(n: number): number {
  let x = Math.imul(n | 0, 0x9e3779b1) ^ 0x85ebca6b;
  x = Math.imul(x ^ (x >>> 15), 0x2c1b3c6d);
  x = Math.imul(x ^ (x >>> 12), 0x297a2d39);
  x ^= x >>> 15;
  return (x >>> 0) / 4294967296;
}

/** Standard normal from two hashes (Box–Muller), deterministic in (k, salt). */
function gauss(k: number, salt: number): number {
  const u1 = Math.max(hash01(k * 2 + salt * 104729), 1e-9);
  const u2 = hash01(k * 2 + 1 + salt * 104729);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(TAU * u2);
}

/** Smooth value noise in [-1, 1] over a continuous coordinate. */
function valueNoise(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const s = f * f * (3 - 2 * f);
  return lerp(hash01(i) - 0.5, hash01(i + 1) - 0.5, s) * 2;
}

// ----------------------------------------------------------------------------
// Rhythm profile and RR generation
// ----------------------------------------------------------------------------

type Kind = 'sinus' | 'af' | 'vt' | 'pea' | 'none';

export interface RhythmProfile {
  rhythm: Rhythm;
  kind: Kind;
  /** Whether this rhythm has discrete QRS complexes. */
  beats: boolean;
  /** Mean RR interval in seconds (0 for vf and asystole). */
  rr0: number;
  /** Effective ventricular rate in per minute (0 for vf and asystole). */
  rate: number;
}

export function profileFor(params: Pick<EcgParams, 'rate' | 'rhythm'>): RhythmProfile {
  const r = params.rhythm;
  let kind: Kind;
  let rate: number;
  switch (r) {
    case 'sinus':
    case 'sinusTachy':
    case 'sinusBrady':
      kind = 'sinus';
      rate = clamp(params.rate, 20, 250);
      break;
    case 'af':
      kind = 'af';
      rate = clamp(params.rate, 40, 220);
      break;
    case 'vt':
      kind = 'vt';
      rate = params.rate >= 120 && params.rate <= 260 ? params.rate : 170;
      break;
    case 'pea':
      kind = 'pea';
      rate = params.rate >= 15 && params.rate <= 60 ? params.rate : 35;
      break;
    case 'vf':
    case 'asystole':
    default:
      kind = 'none';
      rate = 0;
  }
  return { rhythm: r, kind, beats: kind !== 'none', rr0: rate > 0 ? 60 / rate : 0, rate };
}

/**
 * RR interval that precedes beat k (k ≥ 1) for a profile. Sinus uses a telescoping jitter so
 * the mean RR is exact; AF draws from a seeded lognormal; VT and PEA are near-regular.
 */
export function rrFor(p: RhythmProfile, k: number): number {
  const rr0 = p.rr0;
  switch (p.kind) {
    case 'sinus': {
      // Respiratory sinus arrhythmia at ~0.25 Hz plus a little beat-to-beat scatter, larger at slow rates.
      const hrv = clamp(60 / p.rate, 0.4, 1.3);
      const j = (i: number) => rr0 * (0.022 * hrv * Math.sin(TAU * 0.25 * i * rr0 + 0.9) + 0.006 * hrv * (hash01(i * 3 + 1) - 0.5));
      return rr0 + j(k) - j(k - 1);
    }
    case 'af': {
      const sigma = 0.24;
      return rr0 * clamp(Math.exp(sigma * gauss(k, 11)), 0.45, 2.2) / Math.exp(sigma * sigma / 2);
    }
    case 'vt':
      return rr0 * (1 + 0.012 * (hash01(k * 7 + 3) - 0.5));
    case 'pea':
      return rr0 * (1 + 0.16 * (hash01(k * 5 + 2) - 0.5));
    default:
      return Infinity;
  }
}

/** Time of the first R peak in a pure (stateless) schedule. */
const FIRST_R = 0.3;

/** Lazily extended list of R-peak times for a profile. */
class Schedule {
  readonly times: number[] = [FIRST_R];
  constructor(readonly profile: RhythmProfile) {}
  extendTo(t: number): void {
    if (!this.profile.beats) return;
    let last = this.times[this.times.length - 1];
    while (last < t) {
      last += rrFor(this.profile, this.times.length);
      this.times.push(last);
    }
  }
  /** Index of the first beat at or after t (binary search). */
  firstAtOrAfter(t: number): number {
    const a = this.times;
    let lo = 0, hi = a.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (a[mid] < t) lo = mid + 1; else hi = mid; }
    return lo;
  }
}

const scheduleCache = new Map<string, Schedule>();
function scheduleFor(p: RhythmProfile): Schedule {
  const key = `${p.kind}|${p.rr0.toFixed(6)}`;
  let s = scheduleCache.get(key);
  if (!s) {
    if (scheduleCache.size > 48) scheduleCache.clear();
    s = new Schedule(p);
    scheduleCache.set(key, s);
  }
  return s;
}

// ----------------------------------------------------------------------------
// Lead morphology
// ----------------------------------------------------------------------------

interface Amps { P: number; Q: number; R: number; S: number; T: number }

/** Normal 12-lead amplitudes in mV at a mean QRS axis of about +60°. */
const BASE: Record<Lead, Amps> = {
  I:   { P: 0.08,  Q: -0.03, R: 0.55, S: -0.08, T: 0.18 },
  II:  { P: 0.12,  Q: -0.06, R: 0.95, S: -0.10, T: 0.32 },
  III: { P: 0.06,  Q: -0.08, R: 0.45, S: -0.14, T: 0.12 },
  aVR: { P: -0.08, Q: 0.00,  R: 0.12, S: -0.65, T: -0.22 },
  aVL: { P: 0.03,  Q: -0.02, R: 0.35, S: -0.15, T: 0.08 },
  aVF: { P: 0.09,  Q: -0.06, R: 0.70, S: -0.10, T: 0.24 },
  V1:  { P: 0.05,  Q: 0.00,  R: 0.20, S: -0.75, T: -0.10 },
  V2:  { P: 0.06,  Q: 0.00,  R: 0.45, S: -0.85, T: 0.40 },
  V3:  { P: 0.07,  Q: 0.00,  R: 0.75, S: -0.60, T: 0.45 },
  V4:  { P: 0.08,  Q: -0.03, R: 1.20, S: -0.35, T: 0.42 },
  V5:  { P: 0.08,  Q: -0.05, R: 1.10, S: -0.20, T: 0.35 },
  V6:  { P: 0.07,  Q: -0.06, R: 0.85, S: -0.10, T: 0.28 },
};

/** Right-sided chest leads V3R to V6R stand in for V3 to V6: small rS complexes with flat or inverted T. */
const RIGHT: Partial<Record<Lead, Amps>> = {
  V3: { P: 0.05, Q: 0.00, R: 0.25, S: -0.45, T: -0.05 },
  V4: { P: 0.05, Q: 0.00, R: 0.20, S: -0.30, T: -0.04 },
  V5: { P: 0.04, Q: 0.00, R: 0.18, S: -0.22, T: -0.03 },
  V6: { P: 0.04, Q: 0.00, R: 0.15, S: -0.18, T: -0.02 },
};

export interface SampleOptions {
  /** Record V3 to V6 as right-sided chest leads V3R to V6R (RV infarct). */
  rightSided?: boolean;
}

/** Frontal-plane angle of each limb lead in degrees. */
const LIMB_ANGLE: Partial<Record<Lead, number>> = { I: 0, II: 60, III: 120, aVR: -150, aVL: -30, aVF: 90 };

// ----------------------------------------------------------------------------
// The broad complex (VT, PEA): two vectors projected onto each lead
// ----------------------------------------------------------------------------

const DEG = Math.PI / 180;
/** A direction in the body: x to the patient's left, y down (inferior), z forward (anterior). */
interface Vec { x: number; y: number; z: number }
const frontal = (deg: number, size: number, forward: number): Vec => ({ x: size * Math.cos(deg * DEG), y: size * Math.sin(deg * DEG), z: forward });
const dot = (a: Vec, b: Vec) => a.x * b.x + a.y * b.y + a.z * b.z;

/** Horizontal-plane angle of each chest lead in degrees, from the patient's left (V6) round the front to the right of the sternum (V1). */
const CHEST_ANGLE: Partial<Record<Lead, number>> = { V1: 115, V2: 94, V3: 75, V4: 60, V5: 35, V6: 0 };
/** The chest leads sit close to the heart and record larger complexes than the limb leads. */
const CHEST_GAIN: Partial<Record<Lead, number>> = { V1: 1.05, V2: 1.45, V3: 1.55, V4: 1.45, V5: 1.25, V6: 1.05 };

/** The direction a lead looks from, and its gain. Right-sided V3R to V6R mirror V3 to V6 across the sternum. */
function leadAxis(lead: Lead, rightSided = false): { dir: Vec; gain: number } {
  const limb = LIMB_ANGLE[lead];
  if (limb !== undefined) return { dir: frontal(limb, 1, 0), gain: 1 };
  const angle = rightSided && RIGHT[lead] ? 180 - CHEST_ANGLE[lead]! : CHEST_ANGLE[lead]!;
  return { dir: { x: Math.cos(angle * DEG), y: 0, z: Math.sin(angle * DEG) }, gain: CHEST_GAIN[lead] ?? 1 };
}

/**
 * The two halves of a broad ventricular complex as vectors: the early half (the slow start, centred on the R peak time, SD
 * BROAD.earlySd) and the late half (BROAD.lateAt later, SD BROAD.lateSd). A lead the two point towards draws a broad monophasic
 * R, a lead they point away from a QS, a lead between them an RS or a QR; the T wave is discordant to the complex.
 */
const BROAD_VECTORS: Record<'vt' | 'pea', { early: Vec; late: Vec }> = {
  // Monomorphic VT from the left ventricle, as the textbook shows it: a right bundle branch block pattern (a broad monophasic R
  // in V1 and V2, falling across the chest to a QS in V5 and V6) with the axis in the north-west quadrant (negative in I, II
  // and aVF, a dominant R in aVR).
  vt: { early: frontal(-165, 0.8, 0.75), late: frontal(-130, 1, 0.35) },
  // The slow broad complexes of PEA, an idioventricular escape: a left bundle branch block pattern (QS in V1, a broad R in V6)
  // with an inferior axis, so lead II, the monitor's lead, shows a broad upright complex.
  pea: { early: frontal(30, 0.8, -0.35), late: frontal(70, 1, -0.65) },
};

/** The broad complex's timing at its default width (s), its size (mV) and its discordant T. */
const BROAD = { earlySd: 0.03, lateAt: 0.06, lateSd: 0.022, amp: 1.2, tAt: 0.26, tSd: 0.07, t: 0.45 } as const;
/**
 * The default broad QRS in ms, by the convention the narrow complex is built on (its first wave's centre minus 2 SD to its last
 * wave's centre plus 2 SD; the 90 ms default is Q at −26 ms, SD 7, to S at +30 ms, SD 10): −60 ms to +104 ms.
 */
export const BROAD_QRS_MS = Math.round(1000 * (BROAD.lateAt + 2 * BROAD.lateSd + 2 * BROAD.earlySd));
/** The narrowest and widest broad complex the synthesiser draws, in ms (a QRS asked for outside is drawn at the nearer one). */
export const BROAD_QRS_RANGE = [120, 240] as const;

/** The broad complex's width in ms for the params: the QRS asked for within BROAD_QRS_RANGE, else the default. */
export function broadQrsMs(params: Pick<EcgParams, 'qrsMs'>): number {
  const q = params.qrsMs;
  return typeof q === 'number' && Number.isFinite(q) ? clamp(Math.round(q), BROAD_QRS_RANGE[0], BROAD_QRS_RANGE[1]) : BROAD_QRS_MS;
}

/** The broad complex's two vectors, turned in the frontal plane so the mean QRS axis is `axisDeg` when one is asked for. */
function broadVectors(kind: 'vt' | 'pea', axisDeg?: number): { early: Vec; late: Vec; mean: Vec } {
  const { early, late } = BROAD_VECTORS[kind];
  const meanOf = (e: Vec, l: Vec): Vec => {
    const w = BROAD.earlySd + BROAD.lateSd;
    return { x: (BROAD.earlySd * e.x + BROAD.lateSd * l.x) / w, y: (BROAD.earlySd * e.y + BROAD.lateSd * l.y) / w, z: (BROAD.earlySd * e.z + BROAD.lateSd * l.z) / w };
  };
  const mean = meanOf(early, late);
  if (axisDeg === undefined || !Number.isFinite(axisDeg)) return { early, late, mean };
  const turn = axisDeg * DEG - Math.atan2(mean.y, mean.x);
  const c = Math.cos(turn), s = Math.sin(turn);
  const rot = (v: Vec): Vec => ({ x: v.x * c - v.y * s, y: v.x * s + v.y * c, z: v.z });
  const e = rot(early), l = rot(late);
  return { early: e, late: l, mean: meanOf(e, l) };
}

/** The net deflection of a lead's broad complex: each half's height times its width (both halves share the width factor). */
export const broadNet = (m: Pick<LeadMorph, 'bE' | 'bL'>): number => m.bE * BROAD.earlySd + m.bL * BROAD.lateSd;

/** The broad complex in one lead: its early and late halves and its T wave in mV, and its width factor (1 = BROAD_QRS_MS). */
function broadMorph(kind: 'vt' | 'pea', params: EcgParams, lead: Lead, rightSided?: boolean): { bE: number; bL: number; bT: number; bW: number } {
  const v = broadVectors(kind, params.axisDeg);
  const { dir, gain } = leadAxis(lead, rightSided);
  const k = BROAD.amp * gain;
  return { bE: k * dot(v.early, dir), bL: k * dot(v.late, dir), bT: -BROAD.t * k * dot(v.mean, dir), bW: broadQrsMs(params) / BROAD_QRS_MS };
}

/** The QT the T wave can be drawn at (s), and the RR (s) the QT is worked out from (QTc × √RR, Bazett). */
export const QT_RANGE = [0.22, 0.68] as const;
export const RR_FOR_QT = [0.3, 1.6] as const;

/** Everything needed to draw one beat in one lead. Fully numeric so two morphs can be blended. */
export interface LeadMorph {
  P: number; Q: number; R: number; S: number; T: number;
  /** ST level in mV at the J point (positive elevation). */
  st: number;
  /** P-wave centre relative to the R peak, seconds (negative). */
  pC: number;
  /** QRS width factor (1 = 90 ms). */
  qW: number;
  /** T-wave centre and width in seconds. */
  tC: number; tW: number;
  /** 0 narrow, 1 wide bizarre complex (VT/PEA). */
  wide: number;
  /** Overall amplitude scale. */
  amp: number;
  /** The broad complex in this lead: its early and late halves and its discordant T wave (mV), and its width factor. */
  bE: number; bL: number; bT: number; bW: number;
}

export function leadMorph(params: EcgParams, lead: Lead, opts?: SampleOptions): LeadMorph {
  const prof = profileFor(params);
  const base = (opts?.rightSided && RIGHT[lead]) || BASE[lead];
  let { P, Q, R, S, T } = base;

  // Axis: re-project the limb-lead QRS as a dipole when a non-default axis is asked for.
  const angle = LIMB_ANGLE[lead];
  if (params.axisDeg !== undefined && angle !== undefined && Math.abs(params.axisDeg - 60) > 5) {
    const net = 0.9 * Math.cos(((params.axisDeg - angle) * Math.PI) / 180);
    R = 0.12 + Math.max(0, net);
    S = -0.1 + Math.min(0, net);
    Q = net > 0 ? -0.04 : 0;
    T = T * Math.sign(net || 1) * (0.6 + 0.4 * Math.abs(net));
  }

  const rr = prof.rr0 > 0 ? clamp(prof.rr0, RR_FOR_QT[0], RR_FOR_QT[1]) : 1;
  const qtc = (params.qtcMs ?? 420) / 1000;
  const qt = clamp(qtc * Math.sqrt(rr), QT_RANGE[0], QT_RANGE[1]);
  const tC = 0.29 * (qt / 0.42);
  const tW = 0.052 * (qt / 0.42);
  const pC = -(params.prMs ?? 160) / 1000 + 0.015;
  const qW = (params.qrsMs ?? 90) / 90;

  if (prof.kind !== 'sinus') P = 0;
  if (params.tInversion?.includes(lead)) T = -Math.max(0.15, 0.7 * Math.abs(T));

  const st = params.stShift[lead] ?? 0;
  if (st >= 0.2) {
    // Hyperacute: the S wave is swallowed, the T grows broad and tall.
    S *= Math.max(0, 1 - st / 0.3);
    T += 0.8 * st;
  } else if (st > 0) {
    S *= Math.max(0.3, 1 - st / 0.3);
    T += 0.4 * st;
  } else if (st < 0) {
    // Reciprocal: downsloping into a flattened or inverted T.
    T += 1.8 * st;
  }

  const wide = prof.kind === 'vt' || prof.kind === 'pea' ? 1 : 0;
  const amp = prof.kind === 'pea' ? 0.55 : 1;
  const broad = prof.kind === 'vt' || prof.kind === 'pea' ? broadMorph(prof.kind, params, lead, opts?.rightSided) : { bE: 0, bL: 0, bT: 0, bW: 1 };
  return { P, Q, R, S, T, st, pC, qW, tC, tW: st >= 0.2 ? tW * (1 + st) : tW, wide, amp, ...broad };
}

function lerpMorph(a: LeadMorph, b: LeadMorph, m: number): LeadMorph {
  if (m <= 0) return a;
  if (m >= 1) return b;
  return {
    P: lerp(a.P, b.P, m), Q: lerp(a.Q, b.Q, m), R: lerp(a.R, b.R, m), S: lerp(a.S, b.S, m), T: lerp(a.T, b.T, m),
    st: lerp(a.st, b.st, m), pC: lerp(a.pC, b.pC, m), qW: lerp(a.qW, b.qW, m), tC: lerp(a.tC, b.tC, m), tW: lerp(a.tW, b.tW, m),
    wide: lerp(a.wide, b.wide, m), amp: lerp(a.amp, b.amp, m),
    bE: lerp(a.bE, b.bE, m), bL: lerp(a.bL, b.bL, m), bT: lerp(a.bT, b.bT, m), bW: lerp(a.bW, b.bW, m),
  };
}

/** Support of one beat around its R peak, seconds: room for a P wave at a PR of 400 ms and a T wave at a QT of QT_RANGE[1]. */
const BEAT_BEFORE = 0.46;
const BEAT_AFTER = 0.8;

function narrowBeat(m: LeadMorph, u: number): number {
  const w = m.qW;
  let y = g(u, m.P, m.pC, 0.024) + g(u, m.Q, -0.026 * w, 0.007 * w) + g(u, m.R, 0, 0.013 * w) + g(u, m.S, 0.030 * w, 0.010 * w);
  const j = 0.040 * w;
  const tOn = m.tC - 0.055;
  if (m.st !== 0) {
    const seg = 0.5 * (Math.tanh((u - j) / 0.012) - Math.tanh((u - tOn) / 0.045));
    const frac = clamp((u - j) / (tOn - j), 0, 1);
    // Convex rise into the T for hyperacute elevation; a downslope for reciprocal depression.
    const slope = m.st >= 0.2 ? 0.5 : m.st > 0 ? 0.15 : 0.8;
    y += m.st * seg * (1 + slope * frac);
  }
  y += g(u, m.T, m.tC, m.tW);
  return y;
}

function wideBeat(m: LeadMorph, u: number): number {
  const w = m.bW;
  return g(u, m.bE, 0, BROAD.earlySd * w) + g(u, m.bL, BROAD.lateAt * w, BROAD.lateSd * w) + g(u, m.bT, BROAD.tAt + (w - 1) * 0.1, BROAD.tSd);
}

/** One beat's contribution in mV at u seconds from its R peak. */
export function beatValue(m: LeadMorph, u: number): number {
  if (u < -BEAT_BEFORE || u > BEAT_AFTER) return 0;
  const n = m.wide < 1 ? narrowBeat(m, u) : 0;
  const w = m.wide > 0 ? wideBeat(m, u) : 0;
  return m.amp * ((1 - m.wide) * n + m.wide * w);
}

// ----------------------------------------------------------------------------
// Background: noise, wander, fibrillation
// ----------------------------------------------------------------------------

function fWaves(t: number, li: number): number {
  const drift = 1 + 0.03 * Math.sin(TAU * 0.09 * t + li);
  return 0.045 * Math.sin(TAU * 5.6 * drift * t + li * 0.8)
    + 0.03 * Math.sin(TAU * 6.5 * t + 1.1 + li * 0.5)
    + 0.02 * Math.sin(TAU * 7.2 * drift * t + 2.3);
}

function vfWave(t: number, li: number): number {
  let y = 0;
  for (let i = 0; i < 4; i++) {
    const f0 = 4.2 + i * 0.9;
    const fm = 0.11 + 0.07 * i;
    const dev = 1.1;
    const phase = TAU * (f0 * t - (dev / (TAU * fm)) * Math.cos(TAU * fm * t + i * 1.7 + li * 0.3));
    const am = 1 + 0.6 * Math.sin(TAU * (0.23 + 0.05 * i) * t + i * 2.1 + li * 0.2);
    y += (0.17 / (1 + 0.25 * i)) * am * Math.sin(phase);
  }
  y += 0.10 * valueNoise(t * 24 + li * 13.1);
  return y;
}

/** Everything that is not a beat: noise, respiratory wander, f waves, VF, agonal drift. */
export function background(rhythm: Rhythm, lead: Lead, t: number): number {
  const li = LEADS.indexOf(lead);
  const noise = 0.005 * (hash01(Math.round(t * 500) + li * 7919) - 0.5) * 2;
  // Low-amplitude muscle artefact, band-limited around 45 Hz and slowly modulated; more in the limb leads.
  const emg = 0.011 * (li < 6 ? 1.3 : 0.8) * valueNoise(t * 90 + li * 31.7) * (0.55 + 0.45 * valueNoise(t * 0.6 + li * 2.3));
  // Respiratory wander plus a slower drift, as a real recording shows.
  const wander = 0.022 * Math.sin(TAU * 0.25 * t + li * 0.7) + 0.012 * Math.sin(TAU * 0.07 * t + li * 1.9);
  switch (rhythm) {
    case 'af': return noise + emg + wander + fWaves(t, li);
    case 'vf': return noise + emg + vfWave(t, li);
    case 'asystole': return noise * 1.5 + emg + 0.03 * Math.sin(TAU * 0.17 * t + li) + 0.01 * valueNoise(t * 3 + li);
    default: return noise + emg + wander;
  }
}

// ----------------------------------------------------------------------------
// Pure sampling API
// ----------------------------------------------------------------------------

const morphCache = new WeakMap<EcgParams, Map<Lead, LeadMorph>>();
const morphCacheRight = new WeakMap<EcgParams, Map<Lead, LeadMorph>>();
function morphFor(params: EcgParams, lead: Lead, opts?: SampleOptions): LeadMorph {
  const cache = opts?.rightSided ? morphCacheRight : morphCache;
  let m = cache.get(params);
  if (!m) { m = new Map(); cache.set(params, m); }
  let lm = m.get(lead);
  if (!lm) { lm = leadMorph(params, lead, opts); m.set(lead, lm); }
  return lm;
}

function sumBeats(times: number[], from: number, t: number, morph: LeadMorph): number {
  let y = 0;
  for (let i = from; i < times.length; i++) {
    const u = t - times[i];
    if (u < -BEAT_BEFORE) break;
    y += beatValue(morph, u);
  }
  return y;
}

/** Millivolts in `lead` at bay-strip time `tSec` for fixed params. Deterministic. */
export function sampleLead(params: EcgParams, tSec: number, lead: Lead, opts?: SampleOptions): number {
  const prof = profileFor(params);
  let y = background(params.rhythm, lead, tSec);
  if (prof.beats) {
    const s = scheduleFor(prof);
    s.extendTo(tSec + BEAT_BEFORE + 1);
    const from = s.firstAtOrAfter(tSec - BEAT_AFTER);
    y += sumBeats(s.times, from, tSec, morphFor(params, lead, opts));
  }
  return y;
}

/** R-peak times in [fromSec, toSec) for fixed params; empty for vf and asystole. */
export function beatTimes(params: EcgParams, fromSec: number, toSec: number): number[] {
  const prof = profileFor(params);
  if (!prof.beats) return [];
  const s = scheduleFor(prof);
  s.extendTo(toSec + 1);
  const out: number[] = [];
  for (let i = s.firstAtOrAfter(fromSec); i < s.times.length && s.times[i] < toSec; i++) out.push(s.times[i]);
  return out;
}

// ----------------------------------------------------------------------------
// Live stream: params can change without discontinuities
// ----------------------------------------------------------------------------

export interface Beat { t: number; rr: number }

export interface EcgStream {
  /** Millivolts in `lead` (default II) at stream time t. Call with non-decreasing t. */
  at(tSec: number, lead?: Lead): number;
  /** Change rate, rhythm or ST live. Morphology blends over one beat; a rhythm change shows at once. */
  setParams(next: EcgParams, tNow?: number): void;
  /** Scheduled beats in [from, to) with the RR that preceded each; extends the schedule as needed. */
  beats(fromSec: number, toSec: number): Beat[];
  /** R-peak times in [from, to). */
  beatTimes(fromSec: number, toSec: number): number[];
  params(): EcgParams;
  profile(): RhythmProfile;
}

interface Snapshot {
  morph: (lead: Lead) => LeadMorph;
  /** Rhythm mix for the background, weights sum to 1. */
  bg: { rhythm: Rhythm; w: number }[];
  beatWeight: number;
}

export function createEcgStream(initial: EcgParams): EcgStream {
  let cur = initial;
  let prof = profileFor(cur);
  let prevSnap: Snapshot | undefined;
  let tSwitch = -Infinity;
  let blendDur = 1;
  let tLast = 0;

  // Schedule shared by every lead: one list of R peaks, each with the RR that led to it.
  let times: number[] = [FIRST_R];
  let rrs: number[] = [FIRST_R];
  let k = 0;
  let last = FIRST_R;

  const curMorphs = new Map<Lead, LeadMorph>();
  const curMorph = (lead: Lead) => {
    let m = curMorphs.get(lead);
    if (!m) { m = leadMorph(cur, lead); curMorphs.set(lead, m); }
    return m;
  };

  function extend(t: number) {
    if (!prof.beats) return;
    while (last < t + BEAT_BEFORE + 0.2) {
      k++;
      const rr = rrFor(prof, k);
      last += rr;
      times.push(last);
      rrs.push(rr);
    }
    if (times.length > 160 && t - times[48] > 12) {
      times = times.slice(48);
      rrs = rrs.slice(48);
    }
  }

  function firstAtOrAfter(t: number): number {
    let lo = 0, hi = times.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (times[mid] < t) lo = mid + 1; else hi = mid; }
    return lo;
  }

  const mSlow = (t: number) => (prevSnap ? smoothstep((t - tSwitch) / blendDur) : 1);
  const mFast = (t: number) => (prevSnap ? smoothstep((t - tSwitch) / 0.3) : 1);

  function morphAt(lead: Lead, t: number): LeadMorph {
    const c = curMorph(lead);
    if (!prevSnap) return c;
    const m = mSlow(t);
    if (m >= 1) { prevSnap = undefined; return c; }
    return lerpMorph(prevSnap.morph(lead), c, m);
  }

  function bgAt(lead: Lead, t: number): number {
    const c = background(cur.rhythm, lead, t);
    if (!prevSnap) return c;
    const m = mFast(t);
    if (m >= 1) return c;
    let p = 0;
    for (const e of prevSnap.bg) p += e.w * background(e.rhythm, lead, t);
    return lerp(p, c, m);
  }

  function beatWeightAt(t: number): number {
    const c = prof.beats ? 1 : 0;
    if (!prevSnap) return c;
    return lerp(prevSnap.beatWeight, c, mFast(t));
  }

  function at(t: number, lead: Lead = 'II'): number {
    tLast = t;
    extend(t);
    let y = bgAt(lead, t);
    const bw = beatWeightAt(t);
    if (bw > 0 && times.length) {
      const from = firstAtOrAfter(t - BEAT_AFTER);
      y += bw * sumBeats(times, from, t, morphAt(lead, t));
    }
    return y;
  }

  function setParams(next: EcgParams, tNow: number = tLast) {
    if (next === cur) return;
    // Freeze the current blended state so a change mid-blend still starts from what is on screen.
    const frozen = new Map<Lead, LeadMorph>();
    const oldMorphAt = morphAt;
    const bgMix: { rhythm: Rhythm; w: number }[] = [];
    if (prevSnap && mFast(tNow) < 1) {
      const m = mFast(tNow);
      for (const e of prevSnap.bg) bgMix.push({ rhythm: e.rhythm, w: e.w * (1 - m) });
      bgMix.push({ rhythm: cur.rhythm, w: m });
    } else {
      bgMix.push({ rhythm: cur.rhythm, w: 1 });
    }
    const bw = beatWeightAt(tNow);
    for (const lead of LEADS) frozen.set(lead, oldMorphAt(lead, tNow));
    prevSnap = { morph: (lead) => frozen.get(lead)!, bg: bgMix.filter((e) => e.w > 0.02).slice(-2), beatWeight: bw };
    const wsum = prevSnap.bg.reduce((a, e) => a + e.w, 0) || 1;
    for (const e of prevSnap.bg) e.w /= wsum;

    const wasBeating = prof.beats;
    cur = next;
    prof = profileFor(cur);
    curMorphs.clear();
    tSwitch = tNow;
    blendDur = prof.beats ? clamp(prof.rr0, 0.25, 2) : 0.3;

    if (!prof.beats) {
      // Drop beats that have not happened yet; the ones underway finish under the new rhythm.
      const cut = firstAtOrAfter(tNow + 0.02);
      times = times.slice(0, cut);
      rrs = rrs.slice(0, cut);
      last = tNow;
    } else if (!wasBeating) {
      // Coming back from VF or asystole: the first complex lands a little under one RR away.
      last = tNow - prof.rr0 * 0.4;
    }
  }

  function beats(from: number, to: number): Beat[] {
    extend(to);
    const out: Beat[] = [];
    for (let i = firstAtOrAfter(from); i < times.length && times[i] < to; i++) out.push({ t: times[i], rr: rrs[i] });
    return out;
  }

  return {
    at,
    setParams,
    beats,
    beatTimes: (from, to) => beats(from, to).map((b) => b.t),
    params: () => cur,
    profile: () => prof,
  };
}
